import { useState, useEffect, useCallback } from "react";
import { BelegeTab } from "@/pages/admin/BelegeTab";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, Calculator, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EUR = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;
const IN = "mt-1 w-full bg-white border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] px-3 py-2.5 text-sm";
const LB = "text-[10px] uppercase tracking-[0.2em] text-[#757067]";
const CARD = "border border-[#E3DECE] bg-[#FAFAF7] p-5 md:p-6";

const LEER_ZUTAT = { name: "", typ: "zutat", artikelnummer: "", portionsgroesse: "", einheit: "kg", einkauf_menge: 1, einkauf_preis: 0, ausbeute_prozent: 100, lieferant: "" };
const LEER_POS = { zutat_id: "", name: "", einheit: "kg", einkauf_menge: 1, einkauf_preis: 0, ausbeute_prozent: 100, menge_pro_portion: 0 };
const EINHEITEN = ["kg", "g", "l", "ml", "Stück", "Portion"];
const BASIS = { kg: ["g", 1000], g: ["g", 1], l: ["ml", 1000], ml: ["ml", 1], "Stück": ["Stück", 1], Portion: ["Portion", 1] };
const proBasis = (p) => {
  const [, f] = BASIS[p.einheit || "kg"] || ["g", 1];
  const menge = (Number(p.einkauf_menge) || 0) * f;
  if (menge <= 0) return 0;
  const aus = Number(p.ausbeute_prozent) > 0 ? Number(p.ausbeute_prozent) : 100;
  return (Number(p.einkauf_preis) || 0) / menge / (aus / 100);
};
const basisEinheit = (e) => (BASIS[e || "kg"] || ["g"])[0];
const posKosten = (p) => proBasis(p) * (Number(p.menge_pro_portion) || 0);
const LEER_REZEPT = { name: "", gang: "", art: "gang", mengen_als_charge: false, ergibt_portionen: 1, einheit_portion: "Portion", positionen: [] };
const LEER_KALK = {
  name: "", kunde: "", datum: "", personen: 10, rezept_ids: [],
  nebenpositionen: [],
  stundensatz: null,
  arbeitszeit: { einkauf: 2, vorbereitung: 4, fahrt: 1, einsatz: 6, nachbereitung: 1 },
  fahrt_km: 0, spesen: 0, hilfskraft: 0, einsatztage: 1, fixkosten_anteil_prozent: 100,
  verkaufspreis_pro_person: 0,
};

export default function KalkulationTab({ pwd }) {
  const headers = { "X-Admin-Password": pwd };
  const [tab, setTab] = useState("kalkulation");
  const [set0, setSet0] = useState(null);
  const [zutaten, setZutaten] = useState([]);
  const [rezepte, setRezepte] = useState([]);
  const [neueZutat, setNeueZutat] = useState(LEER_ZUTAT);
  const [rezept, setRezept] = useState(LEER_REZEPT);
  const [kalk, setKalk] = useState(LEER_KALK);
  const [ergebnis, setErgebnis] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [e, z, r] = await Promise.all([
        axios.get(`${API}/admin/kalkulation/einstellungen`, { headers }),
        axios.get(`${API}/admin/kalkulation/zutaten`, { headers }),
        axios.get(`${API}/admin/kalkulation/rezepte`, { headers }),
      ]);
      setSet0(e.data); setZutaten(z.data || []); setRezepte(r.data || []);
    } catch {
      toast.error("Kalkulationsdaten konnten nicht geladen werden");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwd]);

  useEffect(() => { load(); }, [load]);

  const speichernEinstellungen = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/kalkulation/einstellungen`, set0, { headers });
      setSet0(data);
      toast.success("Grundlagen gespeichert");
    } catch (err) { toast.error("Fehler: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const zutatSpeichern = async () => {
    if (!neueZutat.name.trim()) { toast.error("Bitte einen Namen eintragen"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/kalkulation/zutaten`, neueZutat, { headers });
      setNeueZutat(LEER_ZUTAT);
      await load();
      toast.success("Zutat gespeichert");
    } catch (err) { toast.error("Fehler: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const zutatLoeschen = async (z) => {
    if (!window.confirm(`„${z.name}" löschen?`)) return;
    await axios.delete(`${API}/admin/kalkulation/zutaten/${z.id}`, { headers });
    load();
  };

  const rezeptSpeichern = async () => {
    if (!rezept.name.trim()) { toast.error("Bitte einen Namen für den Gang eintragen"); return; }
    setBusy(true);
    try {
      if (rezept.id) await axios.put(`${API}/admin/kalkulation/rezepte/${rezept.id}`, rezept, { headers });
      else await axios.post(`${API}/admin/kalkulation/rezepte`, rezept, { headers });
      setRezept(LEER_REZEPT);
      await load();
      toast.success("Gang gespeichert");
    } catch (err) { toast.error("Fehler: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const rezeptLoeschen = async (r) => {
    if (!window.confirm(`Gang „${r.name}" löschen?`)) return;
    await axios.delete(`${API}/admin/kalkulation/rezepte/${r.id}`, { headers });
    if (rezept.id === r.id) setRezept(LEER_REZEPT);
    load();
  };

  const berechnen = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/kalkulation/berechnen`, kalk, { headers });
      setErgebnis(data);
    } catch (err) { toast.error("Fehler: " + (err.response?.data?.detail || err.message)); }
    finally { setBusy(false); }
  };

  const TABS = [
    ["kalkulation", "Kalkulation"],
    ["gaenge", "Gänge & Zusätze"],
    ["zutaten", "Zutaten"],
    ["belege", "Belege & Rezepte einlesen"],
    ["grundlagen", "Grundlagen"],
  ];

  return (
    <div data-testid="kalkulation-tab" className="space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#BA6D52] font-semibold">Kalkulation</p>
          <p className="text-sm text-[#757067] mt-1 max-w-2xl">
            Jedes Gericht einzeln kalkulieren: Zutat, Einkaufspreis und Menge pro Portion eintragen —
            daraus entsteht der Wareneinsatz pro Portion. Die Gänge kommen dann in die Auftrags­kalkulation
            mit Nebenpositionen, Arbeitszeit, Fixkosten und Marge.
          </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-[#E3DECE] pb-3">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`kalk-tab-${id}`}
            className={`text-[10px] uppercase tracking-[0.15em] px-4 py-2 border ${
              tab === id ? "border-[#BA6D52] text-[#BA6D52] bg-[#FAF5F1]" : "border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7]"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ---------------- Grundlagen ---------------- */}
      {tab === "grundlagen" && set0 && (
        <div className={CARD} data-testid="kalk-grundlagen">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-4">Deine Grundlagen</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ["fixkosten_privat", "Private Fixkosten / Monat (€)"],
              ["fixkosten_betrieb", "Betriebliche Fixkosten / Monat (€)"],
              ["einsatztage_monat", "Einsatztage pro Monat"],
              ["stundensatz", "Dein Stundensatz (€)"],
              ["km_satz", "Kilometersatz (€)"],
              ["mwst_prozent", "MwSt (%)"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className={LB}>{label}</label>
                <input type="number" step="0.01" value={set0[k]} data-testid={`kalk-set-${k}`}
                  onChange={(e) => setSet0((s) => ({ ...s, [k]: Number(e.target.value) }))} className={IN} />
              </div>
            ))}
            <div>
              <label className={LB}>Preis-Methode</label>
              <select value={set0.marge_modus} data-testid="kalk-set-marge-modus"
                onChange={(e) => setSet0((s) => ({ ...s, marge_modus: e.target.value }))} className={IN}>
                <option value="faktor">Wareneinsatzfaktor (klassisch 3–4)</option>
                <option value="zuschlag">Zuschlag % auf Selbstkosten</option>
                <option value="ziel_wek">Ziel-Wareneinsatzquote %</option>
              </select>
            </div>
            <div>
              <label className={LB}>Wert dazu</label>
              <input type="number" step="0.1" value={set0.marge_wert} data-testid="kalk-set-marge-wert"
                onChange={(e) => setSet0((s) => ({ ...s, marge_wert: Number(e.target.value) }))} className={IN} />
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-[#2C2A26]">
              Fixkosten gesamt <b>{EUR(set0.fixkosten_monat_gesamt)}</b> / Monat →
              <b> {EUR(set0.fixkosten_pro_einsatztag)}</b> pro Einsatztag
            </p>
            <button onClick={speichernEinstellungen} disabled={busy} data-testid="kalk-set-save"
              className="bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
            </button>
          </div>
        </div>
      )}

      {/* ---------------- Zutaten ---------------- */}
      {tab === "zutaten" && (
        <div className="space-y-6" data-testid="kalk-zutaten">
          <div className={CARD}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-4">Neue Zutat</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {[["zutat", "Zutat"], ["fertigprodukt", "Fertigprodukt / TK-Artikel"]].map(([id, label]) => (
                <button key={id} data-testid={`kalk-zutat-typ-${id}`}
                  onClick={() => setNeueZutat((z) => ({
                    ...z, typ: id, einheit: id === "fertigprodukt" ? "Portion" : "kg",
                  }))}
                  className={`text-[10px] uppercase tracking-[0.15em] px-4 py-2 border ${
                    (neueZutat.typ || "zutat") === id
                      ? "border-[#BA6D52] text-[#BA6D52] bg-[#FAF5F1]"
                      : "border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7]"}`}>
                  {label}
                </button>
              ))}
            </div>
            {neueZutat.typ === "fertigprodukt" && (
              <p className="text-xs text-[#757067] mb-4">
                Beispiel Bienenstich-Quitte: Artikelnummer 11424, Einheit „Portion",
                Einkaufsmenge 52 (Portionen im Gebinde), Einkaufspreis = Preis fürs ganze Gebinde.
                Kennst Du nur den Portionspreis, trag Menge 1 und den Portionspreis ein.
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className={LB}>Zutat</label>
                <input value={neueZutat.name} data-testid="kalk-zutat-name" placeholder="z. B. Lachsfilet"
                  onChange={(e) => setNeueZutat((z) => ({ ...z, name: e.target.value }))} className={IN} />
              </div>
              <div>
                <label className={LB}>Einheit</label>
                <select value={neueZutat.einheit} data-testid="kalk-zutat-einheit"
                  onChange={(e) => setNeueZutat((z) => ({ ...z, einheit: e.target.value }))} className={IN}>
                  {EINHEITEN.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className={LB}>Einkaufsmenge</label>
                <input type="number" step="0.01" value={neueZutat.einkauf_menge} data-testid="kalk-zutat-menge"
                  onChange={(e) => setNeueZutat((z) => ({ ...z, einkauf_menge: Number(e.target.value) }))} className={IN} />
              </div>
              <div>
                <label className={LB}>Einkaufspreis dafür (€)</label>
                <input type="number" step="0.01" value={neueZutat.einkauf_preis} data-testid="kalk-zutat-preis"
                  onChange={(e) => setNeueZutat((z) => ({ ...z, einkauf_preis: Number(e.target.value) }))} className={IN} />
              </div>
              {neueZutat.typ === "fertigprodukt" && (
                <>
                  <div>
                    <label className={LB}>Artikelnummer</label>
                    <input value={neueZutat.artikelnummer} data-testid="kalk-zutat-artikelnummer"
                      placeholder="z. B. 11424"
                      onChange={(e) => setNeueZutat((z) => ({ ...z, artikelnummer: e.target.value }))} className={IN} />
                  </div>
                  <div>
                    <label className={LB}>Portionsgröße</label>
                    <input value={neueZutat.portionsgroesse} data-testid="kalk-zutat-portionsgroesse"
                      placeholder="z. B. 80 ml"
                      onChange={(e) => setNeueZutat((z) => ({ ...z, portionsgroesse: e.target.value }))} className={IN} />
                  </div>
                </>
              )}
              <div>
                <label className={LB}>Ausbeute (%)</label>
                <input type="number" step="1" value={neueZutat.ausbeute_prozent} data-testid="kalk-zutat-ausbeute"
                  onChange={(e) => setNeueZutat((z) => ({ ...z, ausbeute_prozent: Number(e.target.value) }))} className={IN} />
                <p className="text-[11px] text-[#757067] mt-1">
                  100 = kein Verlust. 80 = aus 1.000 g bleiben 800 g verwertbar.
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button onClick={zutatSpeichern} disabled={busy} data-testid="kalk-zutat-save"
                className="bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
                <Plus size={14} /> Zutat anlegen
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {zutaten.length === 0 ? (
              <p className="text-sm text-[#757067]" data-testid="kalk-zutaten-leer">Noch keine Zutat angelegt.</p>
            ) : zutaten.map((z) => (
              <div key={z.id} data-testid={`kalk-zutat-row-${z.id}`}
                className="border border-[#E3DECE] bg-white px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">{z.name}</p>
                  <p className="text-xs text-[#757067] mt-0.5">
                    {z.einkauf_menge} {z.einheit} = {EUR(z.einkauf_preis)} · Ausbeute {z.ausbeute_prozent} % →
                    <b> {(z.preis_pro_einheit || 0).toFixed(4).replace(".", ",")} € / {z.basis || basisEinheit(z.einheit)}</b>
                  </p>
                  {(z.typ === "fertigprodukt" || z.artikelnummer || z.portionsgroesse) && (
                    <p className="text-[11px] text-[#BA6D52] mt-0.5">
                      Fertigprodukt{z.artikelnummer ? ` · Art. ${z.artikelnummer}` : ""}
                      {z.portionsgroesse ? ` · ${z.portionsgroesse}` : ""}
                    </p>
                  )}
                  {z.preis_historie?.length > 0 && (
                    <p className="text-[11px] text-[#757067] mt-0.5" data-testid={`kalk-zutat-historie-${z.id}`}>
                      Preisverlauf: {z.preis_historie.slice(-3).map((h) => (
                        `${(h.datum || "").slice(0, 10)} ${EUR(h.einkauf_preis)}`
                      )).join(" → ")}
                      {z.belege?.length > 0 && ` · ${z.belege.length} Beleg(e)`}
                    </p>
                  )}
                </div>
                <button onClick={() => zutatLoeschen(z)} data-testid={`kalk-zutat-del-${z.id}`}
                  className="border border-[#E3DECE] text-[#757067] hover:bg-red-50 hover:text-red-700 px-3 py-2">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- Gänge ---------------- */}
      {tab === "belege" && <BelegeTab pwd={pwd} onImported={load} />}

      {tab === "gaenge" && (
        <div className="space-y-6" data-testid="kalk-gaenge">
          <div className={CARD}>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-4">
              {rezept.id ? "Gang bearbeiten" : "Neuer Gang"}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={LB}>Name des Gangs</label>
                <input value={rezept.name} data-testid="kalk-rezept-name" placeholder="z. B. Gebratener Lachs"
                  onChange={(e) => setRezept((r) => ({ ...r, name: e.target.value }))} className={IN} />
              </div>
              <div>
                <label className={LB}>Gang-Art (optional)</label>
                <input value={rezept.gang} data-testid="kalk-rezept-gangart" placeholder="z. B. Hauptgang"
                  onChange={(e) => setRezept((r) => ({ ...r, gang: e.target.value }))} className={IN} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              <div>
                <label className={LB}>Art</label>
                <select value={rezept.art || "gang"} data-testid="kalk-rezept-art"
                  onChange={(e) => setRezept((r) => ({ ...r, art: e.target.value }))} className={IN}>
                  <option value="gang">Gang im Menü</option>
                  <option value="zusatz">Zusatz (Gewürzsalz, Brot, Pralinen …)</option>
                </select>
              </div>
              <div className="md:col-span-3 flex items-end">
                <label className="flex items-center gap-2 text-sm text-[#2C2A26] mb-2.5">
                  <input type="checkbox" checked={!!rezept.mengen_als_charge} data-testid="kalk-rezept-charge"
                    onChange={(e) => setRezept((r) => ({ ...r, mengen_als_charge: e.target.checked }))} />
                  Mengen gelten für den ganzen Ansatz (nicht pro Portion)
                </label>
              </div>
              {rezept.mengen_als_charge && (
                <>
                  <div>
                    <label className={LB}>Ansatz ergibt</label>
                    <input type="number" step="1" min="1" value={rezept.ergibt_portionen}
                      data-testid="kalk-rezept-ergibt"
                      onChange={(e) => setRezept((r) => ({ ...r, ergibt_portionen: Number(e.target.value) }))} className={IN} />
                  </div>
                  <div>
                    <label className={LB}>Einheit</label>
                    <select value={rezept.einheit_portion || "Portion"} data-testid="kalk-rezept-einheit-portion"
                      onChange={(e) => setRezept((r) => ({ ...r, einheit_portion: e.target.value }))} className={IN}>
                      {["Portion", "Glas", "Stück", "Flasche", "Tüte"].map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2 flex items-end">
                    <p className="text-xs text-[#757067] mb-2.5">
                      Beispiel Gewürzsalz: 1 kg Meersalz, 200 g Fenchelsaat usw. eintragen — Ansatz ergibt 20 Gläser,
                      dann rechnet das Portal den Preis pro Glas.
                    </p>
                  </div>
                </>
              )}
            </div>

            <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067] mt-6 mb-2">
              Zutaten dieses Gerichts
            </p>
            <p className="text-xs text-[#757067] mb-3">
              Einkauf eintragen (z. B. 1 kg Zander = 24,00 €) und daneben, wie viel du pro Portion
              brauchst (z. B. 150 g). Die Umrechnung macht das Portal.
            </p>
            <div className="space-y-3">
              {rezept.positionen.map((p, i) => {
                const setPos = (patch) => setRezept((r) => {
                  const pos = [...r.positionen];
                  pos[i] = { ...LEER_POS, ...pos[i], ...patch };
                  return { ...r, positionen: pos };
                });
                const bekannt = p.zutat_id ? zutaten.find((x) => x.id === p.zutat_id) : null;
                const basis = bekannt ? bekannt.basis || basisEinheit(bekannt.einheit) : basisEinheit(p.einheit);
                const pro = bekannt ? bekannt.preis_pro_einheit : proBasis(p);
                const kosten = pro * (Number(p.menge_pro_portion) || 0);
                return (
                  <div key={i} className="bg-white border border-[#E3DECE] p-3">
                    <div className="grid grid-cols-2 md:grid-cols-12 gap-3 items-end">
                      <div className="col-span-2 md:col-span-3">
                        <label className={LB}>Zutat</label>
                        <input value={bekannt ? bekannt.name : (p.name || "")} data-testid={`kalk-pos-name-${i}`}
                          placeholder="z. B. Zanderfilet" disabled={!!bekannt}
                          onChange={(e) => setPos({ name: e.target.value })} className={IN} />
                      </div>
                      <div className="md:col-span-2">
                        <label className={LB}>Einkauf</label>
                        <input type="number" step="0.01" data-testid={`kalk-pos-menge-${i}`}
                          value={bekannt ? bekannt.einkauf_menge : p.einkauf_menge} disabled={!!bekannt}
                          onChange={(e) => setPos({ einkauf_menge: Number(e.target.value) })} className={IN} />
                      </div>
                      <div className="md:col-span-1">
                        <label className={LB}>Einheit</label>
                        <select value={bekannt ? bekannt.einheit : p.einheit} disabled={!!bekannt}
                          data-testid={`kalk-pos-einheit-${i}`}
                          onChange={(e) => setPos({ einheit: e.target.value })} className={IN}>
                          {EINHEITEN.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className={LB}>kostet (€)</label>
                        <input type="number" step="0.01" data-testid={`kalk-pos-preis-${i}`}
                          value={bekannt ? bekannt.einkauf_preis : p.einkauf_preis} disabled={!!bekannt}
                          onChange={(e) => setPos({ einkauf_preis: Number(e.target.value) })} className={IN} />
                      </div>
                      <div className="md:col-span-1">
                        <label className={LB}>Ausbeute %</label>
                        <input type="number" step="1" data-testid={`kalk-pos-ausbeute-${i}`}
                          value={bekannt ? bekannt.ausbeute_prozent : p.ausbeute_prozent} disabled={!!bekannt}
                          onChange={(e) => setPos({ ausbeute_prozent: Number(e.target.value) })} className={IN} />
                      </div>
                      <div className="md:col-span-2">
                        <label className={LB}>{rezept.mengen_als_charge ? `Ansatz (${basis})` : `pro Portion (${basis})`}</label>
                        <input type="number" step="0.01" data-testid={`kalk-pos-portion-${i}`}
                          value={p.menge_pro_portion}
                          onChange={(e) => setPos({ menge_pro_portion: Number(e.target.value) })} className={IN} />
                      </div>
                      <div className="md:col-span-1 flex items-end justify-end">
                        <button onClick={() => setRezept((r) => ({ ...r, positionen: r.positionen.filter((_, x) => x !== i) }))}
                          data-testid={`kalk-rezept-pos-del-${i}`}
                          className="border border-[#E3DECE] text-[#757067] hover:bg-red-50 hover:text-red-700 px-3 py-2.5">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                      <p className="text-xs text-[#757067]">
                        {pro > 0 ? `${pro.toFixed(4).replace(".", ",")} € pro ${basis}` : "—"}
                        {" · "}Kosten pro Portion: <b className="text-[#2C2A26]">{EUR(kosten)}</b>
                      </p>
                      <div className="flex items-center gap-2">
                        {zutaten.length > 0 && (
                          <select value={p.zutat_id || ""} data-testid={`kalk-pos-aus-liste-${i}`}
                            onChange={(e) => setPos({ zutat_id: e.target.value })}
                            className="text-xs border border-[#E3DECE] bg-white px-2 py-1.5">
                            <option value="">eigene Eingabe</option>
                            {zutaten.map((x) => <option key={x.id} value={x.id}>aus Liste: {x.name}</option>)}
                          </select>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={() => setRezept((r) => ({ ...r, positionen: [...r.positionen, { ...LEER_POS }] }))}
              data-testid="kalk-rezept-pos-add"
              className="mt-3 text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-4 py-2 inline-flex items-center gap-2">
              <Plus size={13} /> Zutat hinzufügen
            </button>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              {(() => {
                const summe = rezept.positionen.reduce((s2, p) => {
                  const z = p.zutat_id ? zutaten.find((x) => x.id === p.zutat_id) : null;
                  const pro = z ? z.preis_pro_einheit : proBasis(p);
                  return s2 + pro * (Number(p.menge_pro_portion) || 0);
                }, 0);
                const teiler = rezept.mengen_als_charge && Number(rezept.ergibt_portionen) > 0
                  ? Number(rezept.ergibt_portionen) : 1;
                return (
                  <p className="text-sm">
                    {rezept.mengen_als_charge && (
                      <>Ganzer Ansatz: <b>{EUR(summe)}</b> · </>
                    )}
                    Kosten pro {rezept.mengen_als_charge ? (rezept.einheit_portion || "Portion") : "Portion"}:{" "}
                    <b data-testid="kalk-rezept-summe">{EUR(summe / teiler)}</b>
                  </p>
                );
              })()}
              <div className="flex gap-2">
                {rezept.id && (
                  <button onClick={() => setRezept(LEER_REZEPT)} data-testid="kalk-rezept-cancel"
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] px-4 py-2.5">
                    Abbrechen
                  </button>
                )}
                <button onClick={rezeptSpeichern} disabled={busy} data-testid="kalk-rezept-save"
                  className="bg-[#BA6D52] text-white hover:bg-[#A55C43] px-5 py-2.5 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
                  <Save size={14} /> Gang speichern
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {rezepte.length === 0 ? (
              <p className="text-sm text-[#757067]" data-testid="kalk-gaenge-leer">Noch kein Gang angelegt.</p>
            ) : rezepte.map((r) => (
              <div key={r.id} data-testid={`kalk-rezept-row-${r.id}`}
                className="border border-[#E3DECE] bg-white px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">{r.name}{r.gang ? ` · ${r.gang}` : ""}</p>
                  <p className="text-xs text-[#757067] mt-0.5">
                    {r.positionen?.length || 0} Zutaten · <b>{EUR(r.wareneinsatz_pro_portion)}</b> pro{" "}
                    {r.mengen_als_charge ? (r.einheit_portion || "Portion") : "Portion"}
                    {r.mengen_als_charge && ` · Ansatz ${EUR(r.wareneinsatz_charge)} für ${r.ergibt_portionen} ${r.einheit_portion || "Portionen"}`}
                    {r.art === "zusatz" && " · Zusatz"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setRezept({ id: r.id, name: r.name, gang: r.gang, art: r.art || "gang",
                    mengen_als_charge: !!r.mengen_als_charge, ergibt_portionen: r.ergibt_portionen || 1,
                    einheit_portion: r.einheit_portion || "Portion", positionen: r.positionen || [] })}
                    data-testid={`kalk-rezept-edit-${r.id}`}
                    className="text-[10px] uppercase tracking-[0.15em] border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] px-3 py-2">
                    Bearbeiten
                  </button>
                  <button onClick={() => rezeptLoeschen(r)} data-testid={`kalk-rezept-del-${r.id}`}
                    className="border border-[#E3DECE] text-[#757067] hover:bg-red-50 hover:text-red-700 px-3 py-2">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- Kalkulation ---------------- */}
      {tab === "kalkulation" && (
        <div className="space-y-6" data-testid="kalk-rechner">
          <div className={CARD}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <label className={LB}>Bezeichnung</label>
                <input value={kalk.name} data-testid="kalk-name" placeholder="z. B. Firmenfeier Streetec"
                  onChange={(e) => setKalk((k) => ({ ...k, name: e.target.value }))} className={IN} />
              </div>
              <div>
                <label className={LB}>Personen</label>
                <input type="number" min="1" value={kalk.personen} data-testid="kalk-personen"
                  onChange={(e) => setKalk((k) => ({ ...k, personen: Number(e.target.value) }))} className={IN} />
              </div>
              <div>
                <label className={LB}>Einsatztage</label>
                <input type="number" step="0.5" min="0.5" value={kalk.einsatztage} data-testid="kalk-einsatztage"
                  onChange={(e) => setKalk((k) => ({ ...k, einsatztage: Number(e.target.value) }))} className={IN} />
              </div>
            </div>

            <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067] mt-6 mb-2">Gänge im Menü</p>
            <div className="flex flex-wrap gap-2">
              {rezepte.filter((r) => (r.art || "gang") === "gang").length === 0 && (
                <p className="text-sm text-[#BA6D52]">Erst unter „Gänge &amp; Zusätze" einen Gang anlegen.</p>
              )}
              {rezepte.filter((r) => (r.art || "gang") === "gang").map((r) => {
                const aktiv = kalk.rezept_ids.includes(r.id);
                return (
                  <button key={r.id} data-testid={`kalk-gang-pick-${r.id}`}
                    onClick={() => setKalk((k) => ({
                      ...k,
                      rezept_ids: aktiv ? k.rezept_ids.filter((x) => x !== r.id) : [...k.rezept_ids, r.id],
                    }))}
                    className={`text-xs px-3 py-2 border text-left ${aktiv
                      ? "border-[#BA6D52] bg-[#FAF5F1] text-[#BA6D52]" : "border-[#E3DECE] bg-white text-[#757067]"}`}>
                    {r.name} · {EUR(r.wareneinsatz_pro_portion)}
                  </button>
                );
              })}
            </div>

            <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067] mt-6 mb-1">Weitere Kosten pro Person</p>
            <p className="text-xs text-[#757067] mb-3">
              Alles, was pro Gast dazukommt — z. B. Menükarte 3,00 €, Gewürzsalz 5,00 €, Gruß aus der Küche,
              Serviettenring. Beliebig viele Positionen, Du legst sie selbst an.
            </p>
            {kalk.nebenpositionen.length > 0 && (
              <div className="hidden md:flex gap-3 mb-1">
                <span className={`${LB} flex-1`}>Bezeichnung</span>
                <span className={`${LB} w-[150px]`}>€ pro Person</span>
                <span className={`${LB} w-[150px]`}>× {kalk.personen || 1} Personen</span>
                <span className="w-[46px]" />
              </div>
            )}
            <div className="space-y-2">
              {kalk.nebenpositionen.map((n, i) => {
                const setPos = (patch) => setKalk((k) => {
                  const np = [...k.nebenpositionen];
                  np[i] = { ...np[i], ...patch };
                  return { ...k, nebenpositionen: np };
                });
                const rez = n.rezept_id ? rezepte.find((x) => x.id === n.rezept_id) : null;
                const anzahl = Number(n.anzahl_pro_person ?? 1) || 1;
                const preisPP = rez ? (rez.wareneinsatz_pro_portion || 0) * anzahl : (Number(n.preis_pro_person) || 0);
                return (
                  <div key={i} className="bg-white border border-[#E3DECE] p-3">
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex-1 min-w-[180px]">
                        <input value={n.name} data-testid={`kalk-neben-name-${i}`} placeholder="z. B. Gewürzsalz"
                          onChange={(e) => setPos({ name: e.target.value })} className={IN} />
                      </div>
                      <div className="w-[150px]">
                        {rez ? (
                          <div className="mt-1 px-3 py-2.5 text-sm bg-[#FAFAF7] border border-[#E3DECE]"
                            data-testid={`kalk-neben-preis-rezept-${i}`}>
                            {EUR(rez.wareneinsatz_pro_portion)}
                          </div>
                        ) : (
                          <input type="number" step="0.01" min="0" value={n.preis_pro_person}
                            data-testid={`kalk-neben-preis-${i}`}
                            onChange={(e) => setPos({ preis_pro_person: Number(e.target.value) })} className={IN} />
                        )}
                      </div>
                      <div className="w-[150px] pb-2.5 text-sm text-[#757067]" data-testid={`kalk-neben-summe-${i}`}>
                        = {EUR(preisPP * (Number(kalk.personen) || 1))}
                      </div>
                      <button onClick={() => setKalk((k) => ({ ...k, nebenpositionen: k.nebenpositionen.filter((_, x) => x !== i) }))}
                        data-testid={`kalk-neben-del-${i}`}
                        className="mb-1 border border-[#E3DECE] text-[#757067] hover:bg-red-50 hover:text-red-700 px-3 py-2.5">
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <select value={n.rezept_id || ""} data-testid={`kalk-neben-rezept-${i}`}
                        onChange={(e) => {
                          const id = e.target.value;
                          const r2 = rezepte.find((x) => x.id === id);
                          setPos({ rezept_id: id, name: n.name || (r2 ? r2.name : "") });
                        }}
                        className="text-xs border border-[#E3DECE] bg-white px-2 py-1.5">
                        <option value="">Preis selbst eintragen</option>
                        {rezepte.map((x) => (
                          <option key={x.id} value={x.id}>
                            aus Rezept: {x.name} ({EUR(x.wareneinsatz_pro_portion)} pro {x.einheit_portion || "Portion"})
                          </option>
                        ))}
                      </select>
                      {rez && (
                        <>
                          <label className="text-xs text-[#757067]">
                            Anzahl pro Gast
                            <input type="number" step="0.5" min="0" value={n.anzahl_pro_person ?? 1}
                              data-testid={`kalk-neben-anzahl-${i}`}
                              onChange={(e) => setPos({ anzahl_pro_person: Number(e.target.value) })}
                              className="ml-2 w-[70px] border border-[#E3DECE] px-2 py-1" />
                          </label>
                          <span className="text-xs text-[#757067]">
                            {rez.mengen_als_charge
                              ? `Ansatz ${EUR(rez.wareneinsatz_charge)} → ${rez.ergibt_portionen} ${rez.einheit_portion || "Portionen"}`
                              : "Kosten kommen aus dem Rezept"}
                            {" · "}<b className="text-[#2C2A26]">{EUR(preisPP)} pro Gast</b>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {kalk.nebenpositionen.length > 0 && (
              <p className="mt-3 text-sm text-[#2C2A26]" data-testid="kalk-neben-gesamt">
                {(() => {
                  const pp = kalk.nebenpositionen.reduce((s2, n) => {
                    const r2 = n.rezept_id ? rezepte.find((x) => x.id === n.rezept_id) : null;
                    const anz = Number(n.anzahl_pro_person ?? 1) || 1;
                    return s2 + (r2 ? (r2.wareneinsatz_pro_portion || 0) * anz : (Number(n.preis_pro_person) || 0));
                  }, 0);
                  return <>Zusammen <b>{EUR(pp)}</b> pro Person · <b>{EUR(pp * (Number(kalk.personen) || 1))}</b> gesamt</>;
                })()}
              </p>
            )}
            <button onClick={() => setKalk((k) => ({ ...k, nebenpositionen: [...k.nebenpositionen, { name: "", preis_pro_person: 0, rezept_id: "", anzahl_pro_person: 1 }] }))}
              data-testid="kalk-neben-add"
              className="mt-3 text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-4 py-2 inline-flex items-center gap-2">
              <Plus size={13} /> Position hinzufügen
            </button>

            <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067] mt-6 mb-2">
              Stundensatz für diesen Auftrag
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={LB}>€ pro Stunde</label>
                <input type="number" step="0.5" min="0" data-testid="kalk-stundensatz"
                  value={kalk.stundensatz ?? set0?.stundensatz ?? 55}
                  onChange={(e) => setKalk((k) => ({ ...k, stundensatz: Number(e.target.value) }))} className={IN} />
                <p className="text-[11px] text-[#757067] mt-1">
                  Gilt nur für diesen Auftrag. Standard aus den Grundlagen:{" "}
                  {EUR(set0?.stundensatz || 0)}
                </p>
              </div>
              <div className="md:col-span-2 flex items-end">
                {kalk.stundensatz != null && set0 && Number(kalk.stundensatz) !== Number(set0.stundensatz) && (
                  <button onClick={() => setKalk((k) => ({ ...k, stundensatz: null }))}
                    data-testid="kalk-stundensatz-reset"
                    className="mb-1 text-[10px] uppercase tracking-[0.15em] border border-[#E3DECE] text-[#757067] hover:bg-[#FAFAF7] px-4 py-2.5">
                    Zurück auf Standard
                  </button>
                )}
              </div>
            </div>

            <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067] mt-6 mb-2">Arbeitszeit in Stunden</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[["einkauf", "Einkauf"], ["vorbereitung", "Vorbereitung"], ["fahrt", "Fahrt"],
                ["einsatz", "Einsatz"], ["nachbereitung", "Nachbereitung"]].map(([k2, label]) => (
                <div key={k2}>
                  <label className={LB}>{label}</label>
                  <input type="number" step="0.5" value={kalk.arbeitszeit[k2]} data-testid={`kalk-zeit-${k2}`}
                    onChange={(e) => setKalk((k) => ({ ...k, arbeitszeit: { ...k.arbeitszeit, [k2]: Number(e.target.value) } }))}
                    className={IN} />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
              <div>
                <label className={LB}>Fahrt (km)</label>
                <input type="number" step="1" value={kalk.fahrt_km} data-testid="kalk-km"
                  onChange={(e) => setKalk((k) => ({ ...k, fahrt_km: Number(e.target.value) }))} className={IN} />
              </div>
              <div>
                <label className={LB}>Spesen (€)</label>
                <input type="number" step="0.01" value={kalk.spesen} data-testid="kalk-spesen"
                  onChange={(e) => setKalk((k) => ({ ...k, spesen: Number(e.target.value) }))} className={IN} />
              </div>
              <div>
                <label className={LB}>Hilfskraft (€)</label>
                <input type="number" step="0.01" value={kalk.hilfskraft} data-testid="kalk-hilfskraft"
                  onChange={(e) => setKalk((k) => ({ ...k, hilfskraft: Number(e.target.value) }))} className={IN} />
              </div>
              <div>
                <label className={LB}>Fixkosten-Anteil (%)</label>
                <input type="number" step="5" value={kalk.fixkosten_anteil_prozent} data-testid="kalk-fixanteil"
                  onChange={(e) => setKalk((k) => ({ ...k, fixkosten_anteil_prozent: Number(e.target.value) }))} className={IN} />
              </div>
            </div>

            <div className="mt-4 border border-[#E3DECE] bg-[#FAFAF7] p-4" data-testid="kalk-fixanteil-hilfe">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">
                Wie viel soll dieser Auftrag von deinen Fixkosten tragen?
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {[[100, "Voll"], [50, "Halb"], [25, "Gering"], [0, "Keine"]].map(([wert, label]) => (
                  <button key={wert} type="button"
                    data-testid={`kalk-fixanteil-${wert}`}
                    onClick={() => setKalk((k) => ({ ...k, fixkosten_anteil_prozent: wert }))}
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] border transition-colors ${
                      Number(kalk.fixkosten_anteil_prozent) === wert
                        ? "bg-[#2C2A26] text-white border-[#2C2A26]"
                        : "border-[#E3DECE] text-[#757067] hover:border-[#BA6D52]"
                    }`}>
                    {label} · {wert} %
                  </button>
                ))}
              </div>
              <p className="text-sm text-[#757067] mt-3 leading-relaxed">
                Dein Tagesanteil liegt bei <b>{EUR(set0?.fixkosten_pro_einsatztag)}</b>.
                {" "}Bei <b>{Number(kalk.fixkosten_anteil_prozent) || 0} %</b> trägt dieser Auftrag{" "}
                <b>{EUR((set0?.fixkosten_pro_einsatztag || 0) * (Number(kalk.einsatztage) || 1)
                  * (Number(kalk.fixkosten_anteil_prozent) || 0) / 100)}</b>
                {ergebnis?.pro_person?.fixkosten !== undefined
                  && <> — das sind <b>{EUR(ergebnis.pro_person.fixkosten)}</b> pro Person.</>}
              </p>
              <p className="text-xs text-[#8C857A] mt-2 italic">
                Wenn deine laufenden Kosten schon über die Mietkoch-Einsätze gedeckt sind, kannst du hier
                runtergehen — dann rechnet das Menü nur noch Ware, Arbeitszeit und deinen Gewinn.
                Bei 0 % steckt keine Fixkostendeckung mehr im Preis.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
              <div>
                <label className={LB}>Preis-Methode</label>
                <select value={kalk.marge_modus || set0?.marge_modus || "faktor"} data-testid="kalk-marge-modus"
                  onChange={(e) => setKalk((k) => ({ ...k, marge_modus: e.target.value }))} className={IN}>
                  <option value="faktor">Wareneinsatzfaktor</option>
                  <option value="zuschlag">Zuschlag % auf Selbstkosten</option>
                  <option value="ziel_wek">Ziel-Wareneinsatzquote %</option>
                </select>
              </div>
              <div>
                <label className={LB}>Wert dazu</label>
                <input type="number" step="0.1" value={kalk.marge_wert ?? set0?.marge_wert ?? 3}
                  data-testid="kalk-marge-wert"
                  onChange={(e) => setKalk((k) => ({ ...k, marge_wert: Number(e.target.value) }))} className={IN} />
              </div>
              <div>
                <label className={LB}>Oder eigener Preis pro Person (€)</label>
                <input type="number" step="0.01" value={kalk.verkaufspreis_pro_person} data-testid="kalk-eigener-preis"
                  onChange={(e) => setKalk((k) => ({ ...k, verkaufspreis_pro_person: Number(e.target.value) }))} className={IN} />
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button onClick={berechnen} disabled={busy} data-testid="kalk-berechnen"
                className="bg-[#BA6D52] text-white hover:bg-[#A55C43] px-6 py-3 text-xs uppercase tracking-[0.15em] inline-flex items-center gap-2">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />} Berechnen
              </button>
            </div>
          </div>

          {ergebnis && (
            <div className="border border-[#BA6D52] bg-white p-5 md:p-6" data-testid="kalk-ergebnis">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]">Ergebnis</p>

              {ergebnis.warnungen?.length > 0 && (
                <div className="mt-4 space-y-2">
                  {ergebnis.warnungen.map((w, i) => (
                    <p key={i} data-testid={`kalk-warnung-${i}`}
                      className="text-sm text-amber-900 bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
                      <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {w}
                    </p>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067] mb-2">Pro Person</p>
                  {[["Wareneinsatz", ergebnis.pro_person.wareneinsatz],
                    ["Weitere Kosten", ergebnis.pro_person.nebenpositionen],
                    ["Arbeitszeit", ergebnis.pro_person.arbeitszeit],
                    ["Fahrt, Spesen, Hilfe", ergebnis.pro_person.fahrt_spesen_hilfe],
                    ["Fixkostenanteil", ergebnis.pro_person.fixkosten]].map(([l, v]) => (
                    <div key={l} className="flex justify-between text-sm py-1 border-b border-[#F0EBE0]">
                      <span className="text-[#757067]">{l}</span><span className="font-mono">{EUR(v)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm py-2 font-medium">
                    <span>Selbstkosten</span>
                    <span className="font-mono" data-testid="kalk-selbstkosten-pp">{EUR(ergebnis.pro_person.selbstkosten)}</span>
                  </div>
                  <div className="mt-3 bg-[#FAF5F1] border border-[#E3DECE] px-4 py-3">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067]">Verkaufspreis pro Person</p>
                    <p className="font-serif text-3xl text-[#BA6D52] mt-1" data-testid="kalk-vk-pp">
                      {EUR(ergebnis.pro_person.verkaufspreis_netto)}
                    </p>
                    <p className="text-xs text-[#757067]">
                      netto · brutto {EUR(ergebnis.pro_person.verkaufspreis_brutto)} ({ergebnis.kennzahlen.preis_herkunft})
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#757067] mb-2">
                    Gesamt für {ergebnis.personen} Personen
                  </p>
                  {[["Wareneinsatz", ergebnis.gesamt.wareneinsatz],
                    ["Weitere Kosten", ergebnis.gesamt.nebenpositionen],
                    [`Arbeitszeit (${ergebnis.stunden_gesamt} h × ${EUR(ergebnis.stundensatz)})`, ergebnis.gesamt.arbeitszeit],
                    ["Fahrtkosten", ergebnis.gesamt.fahrtkosten],
                    ["Spesen", ergebnis.gesamt.spesen],
                    ["Hilfskraft", ergebnis.gesamt.hilfskraft],
                    ["Fixkostenanteil", ergebnis.gesamt.fixkostenanteil]].map(([l, v]) => (
                    <div key={l} className="flex justify-between text-sm py-1 border-b border-[#F0EBE0]">
                      <span className="text-[#757067]">{l}</span><span className="font-mono">{EUR(v)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm py-2 font-medium">
                    <span>Selbstkosten gesamt</span>
                    <span className="font-mono" data-testid="kalk-selbstkosten">{EUR(ergebnis.gesamt.selbstkosten)}</span>
                  </div>
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-[#757067]">Angebotspreis netto</span>
                    <span className="font-mono" data-testid="kalk-vk-netto">{EUR(ergebnis.gesamt.verkaufspreis_netto)}</span>
                  </div>
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-[#757067]">zzgl. {ergebnis.gesamt.mwst_prozent} % MwSt</span>
                    <span className="font-mono">{EUR(ergebnis.gesamt.mwst_betrag)}</span>
                  </div>
                  <div className="flex justify-between items-baseline border-t border-[#E3DECE] pt-2 mt-2">
                    <span className="text-xs uppercase tracking-[0.2em]">Brutto</span>
                    <span className="font-serif text-2xl text-[#BA6D52]" data-testid="kalk-vk-brutto">
                      {EUR(ergebnis.gesamt.verkaufspreis_brutto)}
                    </span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-[#FAFAF7] border border-[#E3DECE] px-3 py-2">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-[#757067]">Rohertrag</p>
                      <p className="font-mono mt-1">{EUR(ergebnis.gesamt.rohertrag)}</p>
                    </div>
                    <div className="bg-[#FAFAF7] border border-[#E3DECE] px-3 py-2">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-[#757067]">Gewinn nach allen Kosten</p>
                      <p className="font-mono mt-1" data-testid="kalk-deckungsbeitrag">{EUR(ergebnis.gesamt.deckungsbeitrag)}</p>
                    </div>
                    <div className="bg-[#FAFAF7] border border-[#E3DECE] px-3 py-2">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-[#757067]">Wareneinsatzquote</p>
                      <p className="font-mono mt-1">{ergebnis.kennzahlen.wareneinsatzquote_prozent} %</p>
                    </div>
                    <div className="bg-[#FAFAF7] border border-[#E3DECE] px-3 py-2">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-[#757067]">Gewinnmarge</p>
                      <p className="font-mono mt-1">{ergebnis.kennzahlen.gewinnmarge_prozent} %</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
